"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { T, useT } from "@/lib/i18n/T";
import { getFacets, listProducts, searchProducts } from "@/lib/catalog/api";
import type { CatalogFilters } from "@/lib/catalog/api";
import type { Facets, ProductCard } from "@/lib/catalog/types";
import { FilterPanel } from "./FilterPanel";
import { ProductTile, ProductTileSkeleton } from "./ProductTile";
import "./catalog.css";

/**
 * The catalog screen, recovered from the legacy `Search` page and pointed at
 * the real backend.
 *
 * One component serves both jobs the old page served, because they are the same
 * screen with a different source:
 *
 *   search — `/search?q=…`  → hybrid pgvector + full-text, offset paginated
 *   browse — `/fashion/dresses` → section/category filter, keyset paginated
 *
 * The two pagination schemes are not interchangeable. Browse must use the
 * cursor: the scraper inserts rows while a visitor scrolls, and with OFFSET
 * page 2 would repeat rows from page 1 (docs/PROJECT.md §Paginazione).
 */

const PAGE_SIZE = 24;

const SORTS = ["relevance", "newest", "price_asc", "price_desc", "rating"] as const;
type Sort = (typeof SORTS)[number];

/** A query-string number, or undefined when absent or not a number. */
function numberOrUndefined(value: string | null): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** `/fashion?sort=new` comes from the nav; the backend calls it `newest`. */
function normaliseSort(value: string | null): Sort {
  if (value === "new") return "newest";
  return (SORTS as readonly string[]).includes(value ?? "") ? (value as Sort) : "relevance";
}

export type CatalogMode =
  | { kind: "search"; query: string }
  | { kind: "browse"; section: string; category?: string };

export function CatalogView({
  mode,
  locale,
  heading,
}: {
  mode: CatalogMode;
  locale: string;
  /** Title + breadcrumb. Server-rendered for browse pages so crawlers see it. */
  heading?: ReactNode;
}) {
  const t = useT();
  const router = useRouter();
  const params = useSearchParams();

  const sort = normaliseSort(params.get("sort"));

  /**
   * Every filter lives in the query string, never in component state.
   * That is what makes a filtered catalog a link someone can send.
   */
  const selectedCategories = useMemo(() => params.getAll("category"), [params]);
  const selectedBrands = useMemo(() => params.getAll("brand"), [params]);
  const priceMin = numberOrUndefined(params.get("price_min"));
  const priceMax = numberOrUndefined(params.get("price_max"));
  const minRating = numberOrUndefined(params.get("rating"));

  /**
   * One request's worth of results, tagged with the filter combination that
   * produced it. Tagging is what lets `loading` be *derived* rather than set
   * from inside an effect: a filter change makes the key differ from the
   * result's key, which is already "loading" without a second render pass.
   */
  interface Result {
    key: string;
    items: ProductCard[];
    cursor: string | null;
    hasMore: boolean;
    /** Search knows its total; keyset browsing deliberately does not. */
    total: number | null;
    failed: boolean;
  }

  const [result, setResult] = useState<Result | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [retry, setRetry] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Category comes from the route for browse, from the query string for search.
  const categories = useMemo(
    () =>
      mode.kind === "browse"
        ? [mode.category, ...selectedCategories].filter((c): c is string => Boolean(c))
        : selectedCategories,
    [mode, selectedCategories],
  );

  const filters: CatalogFilters = useMemo(
    () => ({
      section: mode.kind === "browse" ? mode.section : undefined,
      category: categories.length ? categories : undefined,
      brand: selectedBrands.length ? selectedBrands : undefined,
      priceMin,
      priceMax,
      minRating,
    }),
    [mode, categories, selectedBrands, priceMin, priceMax, minRating],
  );

  /** Identity of the current selection — changing it invalidates the results. */
  const key = useMemo(() => JSON.stringify([filters, sort, retry]), [filters, sort, retry]);

  const items = result?.key === key ? result.items : [];
  const loading = result?.key !== key;
  const failed = result?.key === key && result.failed;

  /** Fetch one page. `offset`/`cursor` are ignored by the mode that can't use them. */
  const fetchPage = useCallback(
    async (offset: number, cursor: string | null, signal?: AbortSignal) => {
      if (mode.kind === "search") {
        const page = await searchProducts(mode.query, filters, {
          limit: PAGE_SIZE,
          offset,
          signal,
        });
        return {
          items: page.items,
          cursor: null,
          hasMore: offset + page.items.length < page.total,
          total: page.total,
        };
      }
      const page = await listProducts(filters, { sort, cursor, limit: PAGE_SIZE, signal });
      return {
        items: page.items,
        cursor: page.next_cursor,
        hasMore: page.has_more,
        total: null,
      };
    },
    [mode, filters, sort],
  );

  // First page for the current selection. State is only touched in the async
  // callback, never synchronously in the effect body.
  useEffect(() => {
    const controller = new AbortController();
    fetchPage(0, null, controller.signal)
      .then((page) => setResult({ key, ...page, failed: false }))
      .catch((err) => {
        if (controller.signal.aborted || (err as Error)?.name === "AbortError") return;
        setResult({ key, items: [], cursor: null, hasMore: false, total: null, failed: true });
      });
    return () => controller.abort();
  }, [fetchPage, key]);

  /**
   * Facet counts. Fetched for the *section*, not for the active selection, on
   * purpose: counts that shrank as you ticked boxes would make it impossible to
   * see what else is available, and the panel would reshuffle under the cursor.
   */
  useEffect(() => {
    const controller = new AbortController();
    getFacets({ section: mode.kind === "browse" ? mode.section : undefined }, controller.signal)
      .then(setFacets)
      .catch(() => {
        /* the panel is an enhancement — the grid works without it */
      });
    return () => controller.abort();
  }, [mode]);

  /** "Load more" — an event handler, so setting state here costs no extra pass. */
  function loadMore() {
    if (!result || loadingMore) return;
    setLoadingMore(true);
    fetchPage(result.items.length, result.cursor)
      .then((page) =>
        setResult((cur) =>
          cur && cur.key === key ? { ...cur, ...page, items: [...cur.items, ...page.items] } : cur,
        ),
      )
      .catch(() => {
        /* keep what is already on screen; the button stays available */
      })
      .finally(() => setLoadingMore(false));
  }

  function updateQuery(mutate: (p: URLSearchParams) => void) {
    const next = new URLSearchParams(params.toString());
    mutate(next);
    const qs = next.toString();
    router.replace(qs ? `?${qs}` : "?", { scroll: false });
  }

  const activeFilterCount =
    selectedCategories.length +
    selectedBrands.length +
    (priceMin != null || priceMax != null ? 1 : 0) +
    (minRating != null ? 1 : 0);

  const href = (product: ProductCard) => `/${locale}/${product.section}/products/${product.slug}`;

  return (
    <div className="rb-catalog-screen" data-nav-theme="light">
      <div className="rb-catalog-container">
        <FilterPanel
          facets={facets}
          selected={{
            categories: selectedCategories,
            brands: selectedBrands,
            priceMin,
            priceMax,
            minRating,
          }}
          locale={locale}
          section={mode.kind === "browse" ? mode.section : undefined}
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onChange={updateQuery}
        />

        <div className="rb-catalog-results">
          {heading}

          <div className="rb-catalog-head">
            <div className="rb-catalog-count">
              {loading ? (
                <T id="catalog.loading" />
              ) : mode.kind === "search" && result?.total != null ? (
                <T id="catalog.resultsCount" values={{ count: result.total }} />
              ) : (
                <T id="catalog.browsing" />
              )}
            </div>

            <button
              type="button"
              className="rb-catalog-filters-toggle"
              onClick={() => setFiltersOpen(true)}
            >
              <T id="catalog.filters.open" />
              {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
            </button>

            {mode.kind === "browse" && (
              <label className="rb-catalog-sort">
                <span><T id="catalog.sort.label" /></span>
                <select
                  value={sort}
                  onChange={(e) => updateQuery((p) => p.set("sort", e.target.value))}
                >
                  {SORTS.map((s) => (
                    <option key={s} value={s}>
                      {t(`catalog.sort.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {failed && items.length === 0 ? (
            <div className="rb-catalog-empty">
              <h4><T id="catalog.error.title" /></h4>
              <p><T id="catalog.error.body" /></p>
              <button type="button" onClick={() => setRetry((n) => n + 1)}>
                <T id="catalog.error.retry" />
              </button>
            </div>
          ) : !loading && items.length === 0 ? (
            <div className="rb-catalog-empty">
              <h4><T id="catalog.empty.title" /></h4>
              <p><T id="catalog.empty.body" /></p>
            </div>
          ) : (
            <>
              <div className="rb-catalog-grid">
                {items.map((p) => (
                  <ProductTile key={p.id} product={p} href={href(p)} locale={locale} />
                ))}
                {(loading || loadingMore) &&
                  Array.from({ length: PAGE_SIZE }, (_, i) => <ProductTileSkeleton key={`s${i}`} />)}
              </div>

              {result?.hasMore && !loading && (
                <button
                  type="button"
                  className="rb-catalog-more"
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? <T id="catalog.loading" /> : <T id="catalog.loadMore" />}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
